/**
 * Phase 8 T-06 §7 — Smart Match canvas pipeline helpers.
 *
 * A "pipeline" in this build is a client-side session identifier generated
 * on submit of the intake page. The intake prompt and chosen source are
 * persisted in `sessionStorage` under the pipeline id so the canvas can
 * hydrate its agent thread and header chrome after navigation without a new
 * backend round-trip. No server-side pipeline table exists yet — this scope
 * is locked by the Phase 8 "backend is frozen" constraint.
 *
 * `TODO(§8-v2)`: Lift this into a real `ggj_b2b_smart_match_pipelines` row so
 * pipelines survive refresh + can be shared across users.
 */

export type IntakeSource = 'pool' | 'shared' | 'external';

export const INTAKE_QUICK_CHIPS = [
  'Software Engineer',
  'Product Designer',
  'Marketing Lead',
  'Data Scientist',
] as const;

export const SOURCE_OPTIONS: Array<{
  value: IntakeSource;
  label: string;
  disabled?: boolean;
}> = [
  { value: 'pool', label: 'Our candidate pool' },
  { value: 'shared', label: 'Shared pipelines' },
  { value: 'external', label: 'External (paid sources)' },
];

export interface PipelineIntake {
  prompt: string;
  source: IntakeSource;
  /** Optional job id if the canvas should scope matches to a single JD. */
  jobId?: string;
  createdAt: number;
}

const STORAGE_PREFIX = 'ggj.smartmatch.pipeline.';

function safeSession(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function writePipelineIntake(pipelineId: string, intake: PipelineIntake): void {
  const s = safeSession();
  if (!s) return;
  try {
    s.setItem(`${STORAGE_PREFIX}${pipelineId}`, JSON.stringify(intake));
  } catch {
    // quota / private mode — canvas falls back to a generic empty state.
  }
}

export function readPipelineIntake(pipelineId: string): PipelineIntake | null {
  const s = safeSession();
  if (!s) return null;
  try {
    const raw = s.getItem(`${STORAGE_PREFIX}${pipelineId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PipelineIntake>;
    if (typeof parsed.prompt !== 'string' || parsed.prompt.length === 0) return null;
    const source: IntakeSource =
      parsed.source === 'shared' || parsed.source === 'external' ? parsed.source : 'pool';
    return {
      prompt: parsed.prompt,
      source,
      jobId: typeof parsed.jobId === 'string' ? parsed.jobId : undefined,
      createdAt: typeof parsed.createdAt === 'number' ? parsed.createdAt : Date.now(),
    };
  } catch {
    return null;
  }
}

export function sourceLabelFor(source: IntakeSource): string {
  return SOURCE_OPTIONS.find((o) => o.value === source)?.label ?? 'Our candidate pool';
}
